import React, { useState, useEffect, useMemo } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DynamicExportWizard } from '../../components/DynamicExport/DynamicExportWizard';
import {
  TableInfo,
  fetchAvailableTables
} from '../../utils/exportUtils';

const validateTableInfo = (table: any): TableInfo | null => {
  try {
    if (!table || typeof table !== 'object') {
      return null;
    }

    if (table.id === null || table.id === undefined || 
        table.name === null || table.name === undefined) {
      return null;
    }

    const safeId = String(table.id || '');
    const safeName = String(table.name || '');
    const safeDisplayName = table.displayName ? String(table.displayName) : undefined;

    if (!safeId || !safeName) {
      return null;
    }

    return {
      id: safeId,
      name: safeName,
      displayName: safeDisplayName
    };
  } catch (error) {
    console.error('❌ Erro na validação de tabela:', error, table);
    return null;
  }
};

const ReportsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [availableTables, setAvailableTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<TableInfo | null>(null);
  const [showExportWizard, setShowExportWizard] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAvailableTables();
  }, []);

  const loadAvailableTables = async () => {
    try {
      setLoading(true);
      setError(null);
      const tables = await fetchAvailableTables();
      
      const validatedTables = tables
        .map(validateTableInfo)
        .filter((table): table is TableInfo => table !== null);
      
      setAvailableTables(Array.isArray(validatedTables) ? validatedTables : []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar tabelas';
      setError(errorMessage);
      console.error('Erro ao carregar tabelas:', err);
      setAvailableTables([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableSelect = (table: TableInfo) => {
    setSelectedTable(table);
    setShowExportWizard(true);
  };

  const filteredTables = useMemo(() => {
    if (!availableTables || !Array.isArray(availableTables)) {
      return [];
    }
    
    if (availableTables.length === 0) {
      return [];
    }
    
    return availableTables.filter(table => {
      try {
        if (!table || typeof table !== 'object') {
          return false;
        }
        
        if (table.name === null || table.name === undefined) {
          return false;
        }
        
        const tableName = String(table.name || '');
        const tableDisplayName = String(table.displayName || '');
        const search = String(searchTerm || '').toLowerCase();
        
        return tableName.toLowerCase().includes(search) ||
               tableDisplayName.toLowerCase().includes(search);
      } catch (filterError) {
        return false;
      }
    });
  }, [availableTables, searchTerm]);

  const renderTableActions = (table: TableInfo) => (
    <Button
      label="Exportar"
      icon="pi pi-download"
      onClick={() => handleTableSelect(table)}
      size="small"
      className="p-button-success"
    />
  );

  const renderTableName = (table: TableInfo) => (
    <div>
      <div className="font-semibold">{table.displayName || table.name}</div>
      {table.displayName && (
        <small className="text-color-secondary">{table.name}</small>
      )}
    </div>
  );

  const pageHeader = (
    <div className="flex justify-content-between align-items-center">
      <div>
        <h2 className="m-0">Relatórios Dinâmicos</h2>
        <p className="text-color-secondary mt-1 mb-0">
          Selecione uma tabela para criar relatórios personalizados
        </p>
      </div>
      <Button
        label="Atualizar Lista"
        icon="pi pi-refresh"
        onClick={loadAvailableTables}
        loading={loading}
        outlined
      />
    </div>
  );

  if (loading && availableTables.length === 0) {
    return (
      <div className="p-4">
        <Card>
          <div className="flex flex-column align-items-center justify-content-center p-6">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
            <p className="mt-3 text-xl">Carregando tabelas disponíveis...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <Message 
            severity="error" 
            className="w-full"
            content={
              <div>
                <strong>Erro ao carregar dados</strong>
                <p className="mt-2 mb-2">{error}</p>
                <Button
                  label="Tentar Novamente"
                  icon="pi pi-refresh"
                  onClick={loadAvailableTables}
                  size="small"
                />
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Toast />

      <Card header={pageHeader} className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-search"></i>
              </span>
              <InputText
                placeholder="Buscar tabelas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="col-12 md:col-6">
            <div className="flex align-items-center gap-2">
              <Tag 
                severity="info" 
                value={`${filteredTables.length} tabelas disponíveis`} 
              />
              {searchTerm && (
                <Button
                  label="Limpar busca"
                  icon="pi pi-times"
                  size="small"
                  outlined
                  onClick={() => setSearchTerm('')}
                />
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card title="Tabelas Disponíveis">
        {filteredTables.length === 0 ? (
          <div className="text-center p-6">
            <i className="pi pi-table text-4xl text-color-secondary mb-3"></i>
            <p className="text-xl text-color-secondary">
              {searchTerm ? 'Nenhuma tabela encontrada com o termo de busca' : 'Nenhuma tabela disponível'}
            </p>
            {searchTerm && (
              <Button
                label="Limpar busca"
                icon="pi pi-times"
                onClick={() => setSearchTerm('')}
                outlined
              />
            )}
          </div>
        ) : (
          <DataTable
            value={filteredTables}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="p-datatable-hoverable-rows"
            emptyMessage="Nenhuma tabela encontrada"
            responsiveLayout="scroll"
          >
            <Column
              field="name"
              header="Tabela"
              body={renderTableName}
              sortable
              style={{ minWidth: '300px' }}
            />
            
            <Column
              field="id"
              header="ID"
              sortable
              style={{ width: '100px' }}
            />

            <Column
              header="Ações"
              body={renderTableActions}
              style={{ width: '120px', textAlign: 'center' }}
            />
          </DataTable>
        )}
      </Card>

      {selectedTable && showExportWizard && (
        <DynamicExportWizard
          visible={showExportWizard}
          onHide={() => {
            setShowExportWizard(false);
            setSelectedTable(null);
          }}
          tableInfo={selectedTable}
          title="Assistente de Exportação"
          subtitle={`Configurar relatório para a tabela ${selectedTable.displayName || selectedTable.name}`}
        />
      )}
      
      {/* Debug Info */}
      <div style={{ position: 'fixed', top: '10px', right: '10px', background: 'white', padding: '10px', border: '1px solid #ccc', fontSize: '12px' }}>
        <strong>Debug:</strong><br />
        selectedTable: {selectedTable ? selectedTable.name : 'null'}<br />
        showExportWizard: {showExportWizard ? 'true' : 'false'}
      </div>
    </div>
  );
};

export default ReportsPage;